/**
 * Copyright (c) 2022 Cedalo GmbH
 */
import { IconIdea } from '@cedalo/webui/src/components/icons';
import ConfirmDialog from '@cedalo/webui/src/ui/admin/ConfirmDialog';
import { useLocalState } from '@cedalo/webui/src/ui/app/GraphQLWSClient';
import { ConnectionIcon } from '@cedalo/webui/src/ui/sheeteditor/connections/ConnectionIcon';
import { LearnMoreDialog } from '@cedalo/webui/src/ui/sheeteditor/connections/LearnMoreDialog';
import { Form, WIDTH } from '@cedalo/webui/src/ui/utils/Form';
import { Box, Button, FormControl, FormHelperText, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { TestConnection } from './TestAccount';

const ErrorOrContent = ({ errors, children }) => {
	return errors ? (
		<Box sx={{ p: 3, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
			<Typography variant="h5">Unexpected Error</Typography>
			<Typography variant="body1">
				{Array.isArray(errors) ? errors.map(({ message }) => message).join('\n') : errors.message || errors}
			</Typography>
		</Box>
	) : (
		children
	);
};

const formActionCreator = (accountId, delegate) => (formType) =>  async (type, id, value) => {
	const result = delegate.runFormAction(formType, accountId, type, { id, value });
	return result;
};

export const AccountDetailView = ({ accountId, delegate }) => {
	const [subData, subError] = delegate.useAccountUpdateSubscription(accountId);
	const [form, setForm] = useState(null);
	const [name, setLocalName, setRemoteName, isChanged] = useLocalState(null);
	const [errors, setErrors] = useState(null);
	const [nameError, setNameError] = useState(null);
	const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
	const [accountModule, setAccountModule] = useState(null);
	const [learnMoreDialogOpen, setLearnMoreDialogOpen] = useState(false);
	const [connectionError, setConnectionError] = useState(null);

	useEffect(() => {
		const run = async () => {
			const result = await delegate.getAccount(accountId);
			if (result.errors) {
				setErrors(result.errors);
			} else if (result.account) {
				setErrors(null);
				setForm(result.account.form);
				setRemoteName(result.account.name);
				setAccountModule(result.account.module);
			}
		};
		run();
	}, [accountId]);

	useEffect(() => {
		if (subData && subData.accountUpdated.form) {
			const newForm = subData.accountUpdated.form;
			setForm(newForm);
			setRemoteName(subData.accountUpdated.name);
		}
	}, [subData]);

	useEffect(() => {
		if (subError) {
			setErrors(subError);
		}
	}, [subError]);

	const onDelete = async () => {
		const result = await delegate.deleteAccount(accountId);
		if (!result.success) console.error(result.message);
		setShowConfirmDeleteDialog(false);
		window.history.back();
	};

	const setName = async (newName) => {
		if (!name) {
			return;
		}
		const result = await delegate.setAccountName(accountId, newName);
		if (result.success) {
			setRemoteName(result.name);
			setNameError(null);
			setLocalName((current) => (current === result.name ? null : current));
		} else {
			console.error(result.message);
		}
		if (result.code === 'CONFLICT') {
			setNameError('Name is already in use!');
		}
	};

	return (
		<ErrorOrContent errors={errors}>
			{form && accountModule ? (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						height: '100%'
						// margin: '16px'
					}}
				>
					<div style={{ width: 'min(600px, 100%)' }}>
						<Box
							sx={{
								display: 'grid',
								gridGap: '8px',
								gridTemplateColumns: '24px auto 1fr [learn-more] 1fr',
								alignItems: 'center'
							}}
						>
							<ConnectionIcon src={accountModule.logo} />
							<Typography color="text.primary" variant="h6">
								{accountModule.name}
							</Typography>
							{accountModule.isTestable ? (
								<TestConnection
									accountId={accountId}
									resetIndicator={subData}
									onError={setConnectionError}
								/>
							) : null}
							{accountModule.about || accountModule.useCases ? (
								<Tooltip
									enterDelay={300}
									title={
										<FormattedMessage
											id="Extensions.Tooltip.AccountLearnMore"
											defaultMessage="Learn  more about {type}"
											values={{
												type: accountModule.name
											}}
										/>
									}
								>
									<Button
										sx={{
											justifySelf: 'end',
											padding: '6px 8px 6px 10px',
											minWidth: '0px',
											gridColumn: 'learn-more'
										}}
										// color="primary"
										variant="contained"
										// startIcon={<IconIdea />}
										onClick={() => setLearnMoreDialogOpen(true)}
									>
										<IconIdea />
									</Button>
								</Tooltip>
							) : null}
							<LearnMoreDialog
								open={learnMoreDialogOpen}
								onClose={() => setLearnMoreDialogOpen(false)}
								{...accountModule}
							/>
						</Box>
						{connectionError ? (
							<FormControl error>
								<FormHelperText>{connectionError}</FormHelperText>
							</FormControl>
						) : null}
						<Box
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center'
							}}
						>
							<TextField
								value={name}
								label="Name"
								style={{
									maxWidth: WIDTH.medium,
									flex: 1,
									margin: '16px 0px'
								}}
								variant="outlined"
								size="small"
								onChange={(event) => setLocalName(event.target.value)}
								error={!!nameError}
								helperText={nameError}
								onKeyUp={(e) => {
									if (e.key === 'Escape') {
										setLocalName(null);
										setNameError(null);
									} else if (e.key === 'Enter') {
										if (!name) {
											return;
										}
										if (isChanged) {
											setName(name);
										} else {
											setNameError(null);
										}
									}
								}}
								onBlur={() => {
									if (!name) {
										setLocalName(null);
									} else if (isChanged) {
										setName(name);
									} else {
										setNameError(null);
									}
								}}
							/>
							<Button
								style={{ marginLeft: '8px' }}
								variant="outlined"
								onClick={() => setShowConfirmDeleteDialog(true)}
							>
								Delete
							</Button>
						</Box>
						<ConfirmDialog
							open={showConfirmDeleteDialog}
							onConfirm={onDelete}
							title="Delete Account"
							content={`This action can not be undone. Are you sure, you want to delete the Account: '${name}' ?`}
							onCancel={() => setShowConfirmDeleteDialog(false)}
						/>
						<Form {...form} formActionCreator={formActionCreator(accountId, delegate)} />
					</div>
				</Box>
			) : null}
		</ErrorOrContent>
	);
};
